import { type GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'

import { cartPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityCartPage } from '@data/sanity/queries/types/page'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getShopifyDomain } from '@lib/shopify/client'
import { defaultModuleQueryParameters, getCartPage } from '@lib/sanity/page'
import {
  type BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { type Reviews, getReviews } from '@lib/review'
import { type CustomStaticPropsContext } from '@lib/routes'
import { getVideoObjectSchemas } from '@lib/schema/video'

import Layout from '@components/layout'
import Modules from '@modules/modules'
const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type CartProps = BasePageProps & {
  page: SanityCartPage
  site: SanitySiteFragment
  reviews?: Reviews
}

const Cart = ({ draftMode, locale, page, site, reviews }: CartProps) => {
  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [...videoObjectSchemas]

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && <Modules modules={page.modules} reviews={reviews} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          reviews={reviews}
          query={cartPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<CartProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const cartPage = await getCartPage(sanityClient, context.locale)

  if (!cartPage.page) {
    return {
      notFound: true,
    }
  }

  // Get Stamped.io reviews
  const shopifyDomain = getShopifyDomain(context.locale)
  const reviews = await getReviews(
    cartPage.page.modules ?? [],
    shopifyDomain,
    cartPage.site.generalSettings.stampedApiKey,
  )

  return {
    props: {
      ...getPagePropsFromContext(context),
      page: getNonNullValues(cartPage.page),
      site: getNonNullValues(cartPage.site),
      reviews,
    },
    revalidate: 60,
  }
}

export default Cart
